import React, { useEffect, useState } from "react";
import api from "../../services/api";

const FALLBACK_ACTIVITY_TYPES = [
  { id: 1, name: "Phone Call" },
  { id: 2, name: "Email" },
  { id: 3, name: "Meeting" },
  { id: 4, name: "Follow-up" },
  { id: 5, name: "Note Added" },
];

function toLocalInputValue(date) {
  if (!date) return "";
  const d = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(
    d.getHours(),
  )}:${pad(d.getMinutes())}`;
}

function AddNextActivity({
  onSave,
  onCancel,
  saving = false,
  previousActive = null,
}) {
  const [activityTypes, setActivityTypes] = useState([]);
  const [loadingTypes, setLoadingTypes] = useState(true);

  const [formData, setFormData] = useState({
    activityType: "",
    plannedDate: toLocalInputValue(new Date(Date.now() + 24 * 60 * 60 * 1000)),
    notes: "",
    reminderEnabled: true,
    reminderInApp: true,
    reminderEmail: true,
    remindDaysBefore: 1,
    remindOnDueDate: true,
    repeatUntilComplete: true,
    systemAutoComplete: false,
  });

  const [previousDecision, setPreviousDecision] = useState({
    action: "completed",
    remark: "",
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    let cancelled = false;
    api
      .getMasterData("activityTypes")
      .then((data) => {
        if (cancelled) return;
        const list = Array.isArray(data) ? data : [];
        const userSelectable = list.filter(
          (t) => t?.name && !t.hideFromUser && !t.hide_from_user,
        );
        setActivityTypes(
          userSelectable.length ? userSelectable : FALLBACK_ACTIVITY_TYPES,
        );
      })
      .catch(() => {
        if (!cancelled) setActivityTypes(FALLBACK_ACTIVITY_TYPES);
      })
      .finally(() => {
        if (!cancelled) setLoadingTypes(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const setField = (name, value) =>
    setFormData((prev) => ({ ...prev, [name]: value }));

  const handleSubmit = (e) => {
    e.preventDefault();

    const newErrors = {};
    if (!formData.activityType) newErrors.activityType = "Activity type is required";
    if (!formData.plannedDate) {
      newErrors.plannedDate = "Planned date is required";
    } else {
      const d = new Date(formData.plannedDate);
      if (Number.isNaN(d.getTime())) {
        newErrors.plannedDate = "Enter a valid date and time";
      }
    }
    if (
      formData.notes &&
      String(formData.notes).trim().length > 2000
    ) {
      newErrors.notes = "Notes cannot exceed 2000 characters";
    }
    if (
      previousActive &&
      previousDecision.action === "suspended" &&
      !previousDecision.remark.trim()
    ) {
      newErrors.remark = "A remark is required to suspend the previous one";
    }

    if (Object.keys(newErrors).length) {
      setErrors(newErrors);
      return;
    }

    /* Send the raw datetime-local wall-clock value. The backend service
       (nextActivityService.createNextActivity) converts it to UTC using
       the owner's timezone via localToUtcSql. Sending an ISO string
       would cause a double conversion because localToUtcSql strips the
       Z/offset and re-interprets the value as wall-clock in the owner's
       timezone. */
    onSave({
      ...formData,
      plannedDate: formData.plannedDate,
      previousDecision: previousActive
        ? {
            action: previousDecision.action,
            remark: previousDecision.remark.trim() || null,
          }
        : null,
    });
  };

  if (loadingTypes) {
    return (
      <div style={{ padding: "18px" }} className="next-activity-loading">
        <i className="fas fa-spinner fa-spin" /> Loading activity types...
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="add-next-activity-form">
      <div className="form-row">
        <div className="form-group">
          <label>
            Activity Type <span className="required">*</span>
          </label>
          <select
            className={`form-control ${errors.activityType ? "error" : ""}`}
            value={formData.activityType}
            onChange={(e) => setField("activityType", e.target.value)}
            disabled={saving}
          >
            <option value="">Select type</option>
            {activityTypes.map((type) => (
              <option key={type.id || type.name} value={type.name}>
                {type.name}
              </option>
            ))}
          </select>
          {errors.activityType && (
            <div className="error-text">{errors.activityType}</div>
          )}
        </div>

        <div className="form-group">
          <label>
            Planned Date <span className="required">*</span>
          </label>
          <input
            type="datetime-local"
            className={`form-control ${errors.plannedDate ? "error" : ""}`}
            value={formData.plannedDate}
            onChange={(e) => setField("plannedDate", e.target.value)}
            disabled={saving}
          />
          {errors.plannedDate && (
            <div className="error-text">{errors.plannedDate}</div>
          )}
        </div>
      </div>

      <div className="form-group">
        <label>Notes</label>
        <textarea
          className="form-control"
          rows="2"
          value={formData.notes}
          onChange={(e) => setField("notes", e.target.value)}
          placeholder="What's the plan?"
          disabled={saving}
          maxLength={2000}
        />
        {errors.notes && <div className="error-text">{errors.notes}</div>}
      </div>

      <div className="next-activity-reminder-panel">
        <div className="next-activity-reminder-title">
          <i className="fas fa-bell" /> Reminder Settings
        </div>

        <label className="next-activity-check">
          <input
            type="checkbox"
            checked={formData.reminderEnabled}
            onChange={(e) => setField("reminderEnabled", e.target.checked)}
            disabled={saving}
          />
          <span>Send me a reminder</span>
        </label>

        {formData.reminderEnabled && (
          <>
            <div className="next-activity-check-row">
              <label className="next-activity-check">
                <input
                  type="checkbox"
                  checked={formData.reminderInApp}
                  onChange={(e) => setField("reminderInApp", e.target.checked)}
                  disabled={saving}
                />
                <span>In-app</span>
              </label>
              <label className="next-activity-check">
                <input
                  type="checkbox"
                  checked={formData.reminderEmail}
                  onChange={(e) => setField("reminderEmail", e.target.checked)}
                  disabled={saving}
                />
                <span>Email</span>
              </label>
            </div>

            <div className="next-activity-check-row">
              <label className="next-activity-check">
                <input
                  type="checkbox"
                  checked={formData.remindDaysBefore !== null}
                  onChange={(e) =>
                    setField(
                      "remindDaysBefore",
                      e.target.checked ? 1 : null,
                    )
                  }
                  disabled={saving}
                />
                <span>Remind me</span>
              </label>
              {formData.remindDaysBefore !== null && (
                <select
                  className="form-control next-activity-inline-select"
                  value={formData.remindDaysBefore}
                  onChange={(e) =>
                    setField("remindDaysBefore", Number(e.target.value))
                  }
                  disabled={saving}
                >
                  <option value={1}>1 day before</option>
                  <option value={3}>3 days before</option>
                  <option value={7}>7 days before</option>
                </select>
              )}
            </div>

            <label className="next-activity-check">
              <input
                type="checkbox"
                checked={formData.remindOnDueDate}
                onChange={(e) => setField("remindOnDueDate", e.target.checked)}
                disabled={saving}
              />
              <span>On the planned date</span>
            </label>

            <label className="next-activity-check">
              <input
                type="checkbox"
                checked={formData.repeatUntilComplete}
                onChange={(e) =>
                  setField("repeatUntilComplete", e.target.checked)
                }
                disabled={saving}
              />
              <span>Repeat until completed</span>
            </label>

            <label className="next-activity-check">
              <input
                type="checkbox"
                checked={formData.systemAutoComplete}
                onChange={(e) =>
                  setField("systemAutoComplete", e.target.checked)
                }
                disabled={saving}
              />
              <span>Let system auto-complete if I forget</span>
            </label>
          </>
        )}
      </div>

      {previousActive && (
        <div className="next-activity-supersede-panel">
          <div className="next-activity-supersede-title">
            <i className="fas fa-exclamation-triangle" /> Previous Next Contact found
          </div>
          <div className="next-activity-supersede-info">
            <strong>{previousActive.activity_type}</strong>
            {" · "}
            {previousActive.planned_date}
            {previousActive.notes ? ` · "${previousActive.notes}"` : ""}
          </div>

          <div className="next-activity-supersede-options">
            <label className="next-activity-check">
              <input
                type="radio"
                name="previousDecision"
                checked={previousDecision.action === "completed"}
                onChange={() =>
                  setPreviousDecision((p) => ({ ...p, action: "completed" }))
                }
                disabled={saving}
              />
              <span>Mark as Completed</span>
            </label>
            <label className="next-activity-check">
              <input
                type="radio"
                name="previousDecision"
                checked={previousDecision.action === "suspended"}
                onChange={() =>
                  setPreviousDecision((p) => ({ ...p, action: "suspended" }))
                }
                disabled={saving}
              />
              <span>Suspend / Cancel</span>
            </label>
          </div>

          <div className="form-group" style={{ marginTop: 8 }}>
            <label>
              Remark{" "}
              {previousDecision.action === "suspended" && (
                <span className="required">*</span>
              )}
            </label>
            <input
              type="text"
              className={`form-control ${errors.remark ? "error" : ""}`}
              value={previousDecision.remark}
              onChange={(e) =>
                setPreviousDecision((p) => ({ ...p, remark: e.target.value }))
              }
              placeholder={
                previousDecision.action === "completed"
                  ? "Optional remark"
                  : "Reason for suspending (required)"
              }
              disabled={saving}
            />
            {errors.remark && <div className="error-text">{errors.remark}</div>}
          </div>
        </div>
      )}

      <div className="form-actions">
        <button
          type="button"
          className="btn btn-secondary"
          onClick={onCancel}
          disabled={saving}
        >
          Cancel
        </button>
        <button type="submit" className="btn btn-primary" disabled={saving}>
          {saving ? (
            <>
              <i className="fas fa-spinner fa-spin" /> Saving...
            </>
          ) : (
            <>
              <i className="fas fa-save" /> Save Next Contact
            </>
          )}
        </button>
      </div>
    </form>
  );
}

export default AddNextActivity;